(() => {
  if (window.wombatVipBenefits) return;
  window.wombatVipBenefits = true;
  const codes = ['VIP1000-202610', 'VIPSHIP-202610'];
  const campaign = 'wombat-vip-202610:';
  const root = window.Shopify?.routes?.root || '/';
  let running = null;
  let queued = false;
  let bypass = false;
  let checkoutPending = false;
  let lastAttemptFingerprint = null;
  const memory = new Map();
  let removalPending = false;
  try { removalPending = sessionStorage.getItem(campaign + 'removal-pending') === 'true'; } catch {}
  const setRemovalPending = (value) => {
    removalPending = value;
    try { sessionStorage.setItem(campaign + 'removal-pending', String(value)); } catch {}
  };
  const panel = () => document.querySelector('[data-wombat-vip]');
  const readState = (doc = document) => {
    const element = doc.querySelector('[data-wombat-vip-state]');
    if (!element) throw new Error('Missing VIP state');
    return JSON.parse(element.textContent);
  };
  const normalize = (code) => String(code).trim().toUpperCase();
  const isOurs = (code) => codes.includes(normalize(code));
  const cartCodes = (cart) => {
    if (!Array.isArray(cart.discount_codes)) throw new Error('Discount state unavailable');
    return cart.discount_codes.map((entry) => entry.code).filter(Boolean);
  };
  const keyFor = (cart) => {
    if (!cart.token) throw new Error('Cart identity unavailable');
    return campaign + cart.token;
  };
  const intent = (cart) => {
    const key = keyFor(cart);
    try { return JSON.parse(sessionStorage.getItem(key) || 'null') || memory.get(key) || {}; }
    catch { return memory.get(key) || {}; }
  };
  const save = (cart, value) => {
    const key = keyFor(cart);
    memory.set(key, value);
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch {}
  };
  const request = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 7000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      if (!response.ok) throw new Error('Cart request failed');
      return await response.json();
    } finally { clearTimeout(timeout); }
  };
  const status = (state, name, cart) => {
    const element = panel();
    if (!element) return;
    element.querySelector('[data-vip-login]').hidden = state.loggedIn;
    element.querySelector('[data-vip-status]').textContent = state.messages[name] || state.messages.error;
    const button = element.querySelector('[data-vip-toggle]');
    if (button) {
      const optedOut = cart && intent(cart).off;
      button.hidden = !removalPending && (!active(state) || !cart?.item_count);
      button.disabled = name === 'checking';
      button.textContent = removalPending ? state.messages.retryRemoval : optedOut ? state.messages.use : state.messages.skip;
      button.setAttribute('aria-pressed', String(Boolean(optedOut)));
    }
  };
  const fresh = async () => {
    const footer = document.getElementById('main-cart-footer');
    if (!footer?.dataset.id) throw new Error('Missing cart footer');
    const url = new URL(location.href);
    const items = document.getElementById('main-cart-items');
    const ids = [footer.dataset.id, items?.dataset.id].filter(Boolean);
    url.searchParams.set('sections', ids.join(','));
    url.searchParams.set('_vip', Date.now());
    const sections = await request(url);
    const html = sections[footer.dataset.id];
    if (!html) throw new Error('Missing rendered cart footer');
    const doc = new DOMParser().parseFromString(ids.map((id) => sections[id] || '').join(''), 'text/html');
    return { doc, state: readState(doc) };
  };
  const refreshTotals = (doc) => {
    const focused = document.activeElement;
    const id = focused?.id;
    for (const selector of ['#main-cart-footer .js-contents', '#main-cart-items .js-contents']) {
      const current = document.querySelector(selector);
      const next = doc.querySelector(selector);
      if (current && next) current.innerHTML = next.innerHTML;
    }
    if (id && !focused.isConnected) document.getElementById(id)?.focus({ preventScroll: true });
  };
  const update = async (selected) => request(root + 'cart/update.js', {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: selected.join(',') })
  });
  const carryShareableCode = async (code) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 7000);
    try {
      const url = new URL(root + 'discount/' + encodeURIComponent(code), location.origin);
      url.searchParams.set('redirect', root + 'cart');
      const response = await fetch(url, {
        method: 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'text/html' }, signal: controller.signal
      });
      if (!response.ok || new URL(response.url).origin !== location.origin) throw new Error('Discount handoff failed');
      await response.text();
    } finally { clearTimeout(timeout); }
  };
  const carryEligibleCodes = async () => {
    for (const code of codes) {
      const current = await request(root + 'cart.js');
      const currentCodes = cartCodes(current);
      if (removalPending || intent(current).off || currentCodes.some((value) => !isOurs(value))) return current;
      await carryShareableCode(code);
    }
    return request(root + 'cart.js');
  };
  const active = (state) => state.eligible && Date.now() < Date.parse(state.endsAt);
  const reconcile = async (toggle = false, forceRetry = false) => {
    let state;
    let observedCart;
    try {
      const [cart, rendered] = await Promise.all([request(root + 'cart.js'), fresh()]);
      observedCart = cart;
      state = rendered.state;
      refreshTotals(rendered.doc);
      let selected = cartCodes(cart);
      let choice = intent(cart);
      if (choice.version !== 2) {
        choice = { version: 2, off: Boolean(choice.off), attempted: Boolean(choice.applied), confirmedCodes: [] };
        save(cart, choice);
      }
      if (removalPending) { choice = { ...choice, off: true }; save(cart, choice); }
      if (!cart.item_count) { lastAttemptFingerprint = null; save(cart, {}); setRemovalPending(false); status(state, 'empty', cart); return; }
      if (toggle && (active(state) || removalPending)) {
        choice = { ...choice, off: removalPending ? true : !choice.off, suppressReapply: false };
        save(cart, choice);
      }
      const ownPresent = selected.filter(isOurs);
      const foreign = selected.filter((code) => !isOurs(code));
      const confirmedMissing = (choice.confirmedCodes || []).some((code) => !selected.some((value) => normalize(value) === code));
      if (confirmedMissing && !toggle) {
        choice = { ...choice, suppressReapply: true };
        save(cart, choice);
      }
      const fingerprint = JSON.stringify({
        eligible: active(state),
        currency: cart.currency,
        items: (cart.items || []).map((item) => [item.key, item.quantity, item.original_line_price]),
        codes: selected.map(normalize).sort()
      });
      let result = cart;
      let changed = false;
      if (removalPending || choice.off || (!active(state) && choice.attempted)) {
        if (ownPresent.length) {
          setRemovalPending(true);
          result = await update(foreign);
          changed = true;
          if (cartCodes(result).some(isOurs)) throw new Error('Removal not confirmed');
        }
        setRemovalPending(false);
      } else if (active(state) && !foreign.length && !choice.suppressReapply) {
        const confirmedNow = Array.isArray(cart.discount_codes) && codes.every((code) => cart.discount_codes.some((entry) => normalize(entry.code) === code && entry.applicable === true));
        if (!confirmedNow && (forceRetry || toggle || fingerprint !== lastAttemptFingerprint)) {
          lastAttemptFingerprint = fingerprint;
          status(state, 'checking', cart);
          result = await update(codes);
          changed = true;
          choice = { ...choice, attempted: true };
          save(cart, choice);
          const confirmedAjax = Array.isArray(result.discount_codes) && codes.every((code) => result.discount_codes.some((entry) => normalize(entry.code) === code && entry.applicable === true));
          if (!confirmedAjax) result = await carryEligibleCodes();
          if (removalPending) {
            const remaining = cartCodes(result).filter((value) => !isOurs(value));
            result = await update(remaining);
            if (cartCodes(result).some(isOurs)) throw new Error('Removal not confirmed');
            choice = { ...choice, off: true };
            setRemovalPending(false);
          }
          choice = {
            ...choice,
            attempted: true,
            confirmedCodes: (result.discount_codes || []).filter((entry) => isOurs(entry.code) && entry.applicable === true).map((entry) => normalize(entry.code))
          };
          lastAttemptFingerprint = JSON.stringify({
            eligible: active(state),
            currency: result.currency,
            items: (result.items || []).map((item) => [item.key, item.quantity, item.original_line_price]),
            codes: cartCodes(result).map(normalize).sort()
          });
          save(cart, choice);
        }
      }
      if (changed) {
        const latest = await fresh();
        state = latest.state;
        refreshTotals(latest.doc);
      }
      const accepted = Array.isArray(result.discount_codes) ? result.discount_codes : [];
      let name = !state.loggedIn ? 'login' : !state.eligible ? 'notVip' : !active(state) ? 'expired' :
        choice.off ? 'skipped' : cartCodes(result).some((code) => !isOurs(code)) ? 'other' :
        codes.every((code) => accepted.some((entry) => normalize(entry.code) === code && entry.applicable === true)) ? 'applied' : 'pending';
      status(state, name, result);
      return { state, cart: result, choice };
    } catch {
      try { status(state || readState(), removalPending ? 'removalError' : 'error', observedCart); } catch {}
      return null;
    }
  };
  const run = (toggle = false) => {
    if (checkoutPending) return running || Promise.resolve();
    if (running) { if (!toggle) queued = true; return running; }
    running = reconcile(toggle).finally(() => {
      running = null;
      if (queued && !checkoutPending) { queued = false; run(); }
    });
    return running;
  };
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-vip-toggle]');
    if (!button) return;
    event.preventDefault();
    if (removalPending || button.getAttribute('aria-pressed') !== 'true') setRemovalPending(true);
    if (running) { queued = true; button.disabled = true; return; }
    button.disabled = true;
    run(true);
  });
  document.addEventListener('submit', async (event) => {
    if (event.target.id !== 'cart' || event.submitter?.name !== 'checkout' || bypass) return;
    event.preventDefault();
    if (checkoutPending) return;
    checkoutPending = true;
    queued = false;
    try {
      if (running) await running;
      await reconcile(false, true);
      if (removalPending) return;
      const form = document.getElementById('cart');
      const submitter = document.getElementById('checkout');
      if (form && submitter && !submitter.disabled) {
        bypass = true;
        form.requestSubmit(submitter);
      }
    } finally { bypass = false; checkoutPending = false; }
  }, true);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'wombat-vip-benefits') run();
    });
  }
  document.addEventListener('close', (event) => { if (event.target.matches?.('shopify-account')) run(); }, true);
  window.addEventListener('pageshow', (event) => { if (event.persisted) run(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) run(); });
  run();
})();

